import React from "react";
import { Popover, OverlayTrigger, Button as BootstrapButton, Panel } from "react-bootstrap";
import Toggle from "components/Inputs/Toggle/Toggle";

import { Button, Icon, getPrettyNumber, Accordion } from "aible";
import Select from "react-select";
import { isEnabled } from "utils/featureFlags";

import AibleStep from "containers/CreateWizard/AibleStep";

import SelectDataset from "components/CreateWizard/Academic/SelectDataset";
import ProblematicPredictors from "components/CreateWizard/Academic/ProblematicPredictors";
import { ProblematicPredictorLabel, ProblematicPredictorBar } from "components/CreateWizard/Dataset/Correlation";

import RetargetOutcomeModal from "containers/UnifiedProject/SelectData/RetargetOutcomeModal";
import ForecastContainer from "containers/UnifiedProject/SelectData/Forecast";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfo } from "@fortawesome/free-solid-svg-icons";

import { DATA_CHECKS } from "containers/Home/reducer";

import styles from "./style.module.css";

function handlePlurals(count, value = "", noPlural = false) {
  return value + (noPlural || count === 1 ? "" : "s");
}

const VALIDATION_MESSAGES = [
  {
    message: ["too many values", "It would be discarded."],
    type: "high cardinality field",
    action: "would be ignored"
  },
  {
    message: ["It would be discarded."],
    type: "column",
    action: "would be ignored"
  },
  {
    message: ["missing values"],
    type: "column",
    count: 0,
    action: "with missing values would be updated"
  },
  {
    message: ["too many values", "most informative values", "flagged and included in the modeling step"],
    type: "high cardinality field",
    action: "would be updated"
  }
];

function checkMessage(message, checkArr) {
  for (let i = 0; i < checkArr.length; i++) {
    if (!message.toLowerCase().includes(checkArr[i].toLowerCase())) {
      return false;
    }
  }
  return true;
}

function parseValidation(updatedColumns) {
  const retValidation = VALIDATION_MESSAGES.map((validation) => ({
    ...validation,
    value: []
  }));
  const extra = {
    type: "column",
    action: "would be updated for other reasons",
    value: []
  };
  updatedColumns.forEach((col) => {
    let found = false;
    const message = col.metadata && col.metadata.message ? col.metadata.message : col.message;

    for (let i = 0; i < retValidation.length; i++) {
      if (!found && checkMessage(message, retValidation[i].message)) {
        retValidation[i].value.push(message);
        found = true;
        if (retValidation) break;
      }
    }
    if (!found) {
      extra.value.push(message);
    }
  });
  if (retValidation.length < 1) {
    extra.action = "would be updated";
  }
  retValidation.push(extra);
  return retValidation.filter((elem) => elem.value.length);
}

export default class SelectData extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isUpdatingTargetValue: false
    };
  }

  checkIsProblematicPredictor(column) {
    const { problematicPredictors } = this.props;
    if (problematicPredictors) {
      for (let i = 0; i < problematicPredictors.length; i++) {
        if (problematicPredictors[i].label === column) {
          return column + " might also be a Problematic Predictor";
        }
      }
    }
    return undefined;
  }

  renderUserAdjustments() {
    const {
      blueprintId,
      id,
      isShow,
      options,

      step,
      index,

      selectedDataset,
      ignored,
      transformed,

      datasetView,
      updateSelection,

      isAiReady,
      aiReadyFields
    } = this.props;

    const dataChecks = DATA_CHECKS;

    const updatedColumns = (selectedDataset && selectedDataset.warnings) || [];
    const ignoredFields = (Object.keys(ignored) || []).filter((key) => ignored[key]);
    const ignoredColumns = ignoredFields.concat(
      updatedColumns.filter((w) => w.message.includes("ignored")).map((w) => w.message)
    );

    return (
      <React.Fragment>
        <ReviewDatasetHeader
          error={!isAiReady}
          header={"User Adjustments"}
          icon={
            <Icon
              type={"list"}
              isRound
              background={transformed.length || ignoredFields.length ? "green" : "light-grey"}
              style={{ padding: "5px" }}
              size={24}
            />
          }
        />
        <div className={styles.datasetReviewContent}>
          {transformed.length || ignoredFields.length ? (
            <React.Fragment>
              {transformed.length ? (
                <ReviewDatasetItem
                  text={transformed.length + (" column" + handlePlurals(transformed.length) + " added")}
                  overlayTitle={handlePlurals(transformed.length, "Column") + " added"}
                  overlay={
                    <div className={styles.datasetReviewPopup}>
                      {transformed.map((w, i) => (
                        <div className={styles.datasetReviewRow} key={i}>
                          <span>
                            {(w.fieldOne && w.fieldOne.label ? w.fieldOne.label : "") +
                              " " +
                              (w.operation && w.operation.label && w.operation.label !== "Interaction"
                                ? w.operation.label
                                : "and") +
                              " " +
                              (w.fieldTwo && w.fieldTwo.label ? w.fieldTwo.label : "")}
                          </span>
                        </div>
                      ))}
                    </div>
                  }
                />
              ) : (
                ""
              )}
              {ignoredColumns.length ? (
                <ReviewDatasetItem
                  text={ignoredColumns.length + (" column" + handlePlurals(ignoredColumns.length) + " ignored")}
                  overlayTitle={handlePlurals(transformed.length, "Column") + " ignored"}
                  overlay={
                    <div className={styles.datasetReviewPopup}>
                      {ignoredColumns.map((w, i) => (
                        <div className={styles.datasetReviewRow} key={i + w}>
                          <span>{w}</span>
                        </div>
                      ))}
                    </div>
                  }
                />
              ) : (
                ""
              )}
            </React.Fragment>
          ) : (
            <div className={styles.selectSubheader} style={{ marginLeft: "0px" }}>
              {"No user adjustments have been made."}
            </div>
          )}
        </div>
      </React.Fragment>
    );
  }

  renderDatasetInfo() {
    const {
      blueprintId,
      id,
      runId,
      isShow,
      options,

      step,
      index,

      selectedDataset,
      outcomeType,

      selectedSuccess,
      successOptions,
      successOptionsWarning,
      updateSuccess,
      updateSplitColumn,
      ignored,
      updateIgnored,
      transformed,
      splitOptions,
      splitColumn,
      datasetView,
      updateSelection,
      isRetrain,
      isAiReady,
      hasIgnoredAiReady,
      aiReadyFields,
      notAiReadyFields,
      aiReadyWarning,
      isMultiCategoryOutcome,

      problematicPredictors,
      predictors,

      customMappings,
      customMappingOptions,
      validCustomMappings,
      setCustomMapping,

      isDataMode,
      noOutcome,
      isEditBlueprint,
      blueprint,

      project,
      createSplits,
      businessMode,

      isForecastReady,
      isForecast,
      forecastOptions,
      forecastTimeCol,
      updateIsForecast,
      updateForecastTimeCol,
      updateForecastMode,
      isModelsNext,
      isNext,
      createAndUpdateProject,
      enableFairnessMetrics,
      updateEnableFairnessMetrics,

      hasUpdatedOutcomeSuccess,
      outcomeSuccess,
      updateTargetValue
    } = this.props;

    const { isUpdatingTargetValue } = this.state;

    const hasAiMetrics =
      isAiReady !== undefined && aiReadyFields && notAiReadyFields && (aiReadyFields.length || notAiReadyFields.length);

    const isRegressionDataset = outcomeType === "number";
    const isSuccessSelected = isDataMode || (selectedDataset && (selectedSuccess || isRegressionDataset));

    const dataChecks = DATA_CHECKS;
    const dataChecksPassed = dataChecks.length;

    const rowCount = selectedDataset && selectedDataset.rowCount;
    const columnCount = selectedDataset && Object.keys(selectedDataset.fields).length;

    const updatedColumns = (selectedDataset && selectedDataset.warnings) || [];
    const ignoredFields = (Object.keys(ignored) || []).filter((key) => ignored[key]);
    const ignoredColumns = ignoredFields.concat(
      updatedColumns.filter((w) => w.message.includes("ignored")).map((w) => w.message)
    );

    const groupedUpdates = parseValidation(updatedColumns);

    const stepMessage = hasAiMetrics
      ? isAiReady
        ? "Your dataset is AI ready for predicting " + selectedDataset.outcome + "!"
        : hasIgnoredAiReady
          ? "Unknown AI readiness due to ignored fields"
          : "Your dataset is not AI ready"
      : "No AI readiness metrics found";

    const hasProjectSplits = project && project.segments && project.segments.length > 1;
    const overrideSplitColumn = hasProjectSplits
      ? project.segments.reduce(
          (acc, seg) => (acc == undefined && seg.conditions && seg.conditions.length ? seg.conditions[0].column : acc),
          undefined
        )
      : undefined;

    if (!selectedSuccess?.label && selectedSuccess?.value) selectedSuccess.label = selectedSuccess?.value;

    let isNotMatchingColumn = false;
    if (splitColumn && selectedDataset?.fields?.[splitColumn] === undefined) {
      isNotMatchingColumn = true;
    }

    return (
      <div className={styles.dataReviewColumnsWrapper}>
        {!selectedDataset || isDataMode || noOutcome || isRetrain ? (
          ""
        ) : selectedDataset ? (
          <div className={styles.chooseSuccess}>
            <div className={styles.chooseSuccessWrapper}>
              <Icon
                type={selectedSuccess || isDataMode || isRegressionDataset ? "check" : "warning"}
                isRound={selectedSuccess || isDataMode || isRegressionDataset ? true : undefined}
                background={selectedSuccess || isDataMode || isRegressionDataset ? "green" : undefined}
                style={{ marginRight: "6px" }}
                size={24}
              />
              <div className={styles.datasetReviewHeader} style={{ margin: "0px" }}>
                {isForecastReady ? (
                  <div className={styles.selectWrapper + " " + styles.selectPredictMode}>
                    <Select
                      value={
                        isForecast ? { label: "Forecast", value: "Forecast" } : { label: "Predict", value: "Predict" }
                      }
                      onChange={(o) => updateIsForecast(o && o.value === "Forecast")}
                      options={[
                        { label: "Forecast", value: "Forecast" },
                        { label: "Predict", value: "Predict" }
                      ]}
                      className={styles.outcomePicker}
                      menuPlacement="bottom"
                      isDisabled={!isNaN(id)}
                    />
                  </div>
                ) : (
                  "Predict"
                )}
                <span style={{ margin: "0px 6px" }}>
                  {isNaN(id) ? (
                    <RetargetOutcomeModal id={selectedDataset.id} runId={runId}>
                      <a className={styles.retargetLink}>
                        {selectedDataset.outcome === undefined ? "No Outcome Selected" : selectedDataset.outcome}
                      </a>
                    </RetargetOutcomeModal>
                  ) : (
                    selectedDataset.outcome
                  )}
                </span>
                {selectedDataset.outcome === undefined || isDataMode || isRegressionDataset || isForecast ? "" : "is:"}
                {isForecast ? (
                  <div className={styles.forecastDateWrapper}>
                    <div className={styles.forecastDateText}>{" using "}</div>
                    <div className={styles.selectWrapper + " " + styles.selectPredictMode} style={{ width: 200 }}>
                      <Select
                        value={{ label: forecastTimeCol, value: forecastTimeCol }}
                        onChange={(o) => updateForecastTimeCol(o.value)}
                        options={forecastOptions}
                        className={styles.outcomePicker}
                        placeholder="Select Column"
                        menuPlacement="bottom"
                        isDisabled={!isNaN(id)}
                      />
                    </div>
                    <div className={styles.forecastDateText}>{" as the date column "}</div>
                  </div>
                ) : (
                  ""
                )}
              </div>
              {selectedDataset.outcome === undefined || isDataMode || isRegressionDataset || isForecast ? (
                ""
              ) : (
                <div className={styles.selectWrapper}>
                  <Select
                    key={selectedSuccess ? "success" : "undefined"}
                    value={selectedSuccess}
                    onChange={(value) => updateSuccess(value)}
                    options={successOptions}
                    className={styles.outcomePicker}
                    placeholder="Select Value"
                    menuPlacement="bottom"
                    // isMulti={successOptions && successOptions.length > 2}
                    isDisabled={!isNaN(id)}
                  />
                </div>
              )}
              {selectedDataset &&
                hasUpdatedOutcomeSuccess &&
                outcomeSuccess?.[selectedDataset?.target]?.value &&
                successOptions?.length > 2 &&
                !isForecast &&
                !isNext && (
                  <div className={styles.showMore}>
                    <Button
                      disabled={isUpdatingTargetValue}
                      onClick={() => {
                        this.setState({ isUpdatingTargetValue: true });
                        updateTargetValue(selectedDataset?.id, outcomeSuccess[selectedDataset?.target].value).then(
                          () => {
                            this.setState({ isUpdatingTargetValue: false });
                          }
                        );
                      }}
                      style={{ marginLeft: "10px" }}
                    >
                      {isUpdatingTargetValue ? (
                        <React.Fragment>
                          <LoadingIndicator
                            style={{
                              width: "20px",
                              height: "18px",
                              transform: "scale(0.20)",
                              margin: "-5px 5px 5px -5px"
                            }}
                          />
                          {"Analyzing..."}
                        </React.Fragment>
                      ) : (
                        "Analyze Target Value"
                      )}
                    </Button>
                  </div>
                )}
            </div>
            {successOptionsWarning ? (
              <div className={styles.successWarning}>
                <Icon type={"warning"} size={14} style={{ display: "inline-block" }} />
                <span>{successOptionsWarning}</span>
              </div>
            ) : (
              ""
            )}
          </div>
        ) : (
          ""
        )}
        <div className={styles.dataReviewColumns}>
          {/* <ReviewDatasetColumn>
            <div className={styles.datasetReviewHeader}>
              <span className={globalStyles.h1 + " "  + globalStyles.bold}>{dataChecksPassed}</span>
              {"Data Validation Checks Completed"}
            </div>
            <div className={styles.datasetReviewContent}>
              {dataChecks.map((c, i) => (
                <div className={styles.datasetReviewRow} key={i}>
                  <div className={styles.datasetReviewCheck}>
                    <Icon type={"check"} size={10}/>
                  </div>
                  <span>{c}</span>
                </div>
              ))}
            </div>
          </ReviewDatasetColumn> */}
          {isDataMode || !selectedDataset || !customMappings.length ? (
            ""
          ) : (
            <ReviewDatasetColumn>
              <ReviewDatasetHeader
                icon={validCustomMappings ? undefined : <Icon type={"target"} size={24} />}
                header={"Business Assumption Field Mapping"}
              />
              {customMappings.map((cMap, i) => {
                return (
                  <React.Fragment key={"cMap-" + i}>
                    <div style={{ fontWeight: "bold" }}>{cMap.label}</div>
                    <div>{"Use the value found in the column:"}</div>
                    <div className={styles.splitField}>
                      <Select
                        options={customMappingOptions}
                        onChange={(val) => setCustomMapping(cMap.label, val ? val.value : val)}
                        value={cMap ? { label: cMap.value, value: cMap.value } : undefined}
                      />
                    </div>
                  </React.Fragment>
                );
              })}
            </ReviewDatasetColumn>
          )}
          <ReviewDatasetColumn>
            <Accordion
              header={
                <ReviewDatasetHeader error={false} header={"Data Validation"} style={{ marginBottom: "0px" }}>
                  {businessMode ? (
                    <React.Fragment>
                      <span>
                        {getPrettyNumber(dataChecks.length) +
                          "  " +
                          handlePlurals(dataChecks.length, "Data Validation Test") +
                          " Passed"}
                      </span>
                    </React.Fragment>
                  ) : undefined}
                </ReviewDatasetHeader>
              }
              shrinkHeader
            >
              <div className={styles.selectSubheader} style={{ marginLeft: "0px", marginTop: "15px" }}>
                {updatedColumns.length +
                  " " +
                  handlePlurals(updatedColumns.length, "update") +
                  " will be automatically applied when using the dataset to address the following issues "}
              </div>
              <div className={styles.datasetReviewContent}>
                <ReviewDatasetItem
                  text={getPrettyNumber(columnCount) + (" Column" + handlePlurals(columnCount) + " Scanned")}
                />
                <ReviewDatasetItem text={getPrettyNumber(rowCount) + (" Row" + handlePlurals(rowCount) + " Scanned")} />
                <ReviewDatasetItem
                  text={
                    getPrettyNumber(dataChecks.length) +
                    "  " +
                    handlePlurals(dataChecks.length, "Data Check") +
                    " Completed"
                  }
                  overlayTitle={"Data Checks"}
                  overlay={
                    <div className={styles.datasetReviewPopup}>
                      {DATA_CHECKS.map((w, i) => (
                        <ReviewDatasetItem text={w} key={i + w} />
                      ))}
                    </div>
                  }
                />
                {groupedUpdates.length ? (
                  <React.Fragment>
                    <div className={styles.datasetReviewDivider} />
                    <div className={styles.datasetReviewSubContent}>
                      {groupedUpdates.map((update, i) => {
                        const updateText =
                          getPrettyNumber(update.value.length) +
                          " " +
                          (handlePlurals(update.value.length, update.type, update.noPlural) + " " + update.action);
                        return (
                          <ReviewDatasetItem
                            key={update.type + i}
                            text={<strong>{updateText}</strong>}
                            overlayTitle={updateText}
                            overlay={
                              <div className={styles.datasetReviewPopup}>
                                {update.value.map((w, i) => (
                                  <div className={styles.datasetReviewRow} key={i + w}>
                                    <span>{w}</span>
                                  </div>
                                ))}
                              </div>
                            }
                          />
                        );
                      })}
                    </div>
                  </React.Fragment>
                ) : (
                  ""
                )}
              </div>
            </Accordion>
            <Accordion
              header={
                <ReviewDatasetHeader
                  icon={
                    isMultiCategoryOutcome || (!isAiReady && hasIgnoredAiReady) ? (
                      <Icon type={"warning"} size={22} />
                    ) : undefined
                  }
                  error={!isAiReady && !hasIgnoredAiReady}
                  header={"AI Validation"}
                  style={{ marginBottom: "0px" }}
                >
                  <span>
                    {isAiReady ? `AI Validation Passed` : hasIgnoredAiReady ? `AI Validation Unknown` : `AI Validation`}
                  </span>
                </ReviewDatasetHeader>
              }
              shrinkHeader
            >
              <div className={styles.selectSubheader} style={{ marginLeft: "0px", marginTop: "15px" }}>
                {hasAiMetrics
                  ? !isAiReady && hasIgnoredAiReady
                    ? "Unknown AI readiness due to ignored fields"
                    : "Aible scanned the dataset for AI readiness and found that your outcome" +
                      " is" +
                      (isAiReady ? "" : " not") +
                      " appropriate for AutoML modeling"
                  : !isMultiCategoryOutcome
                    ? "No AI readiness metrics found." +
                      (!aiReadyWarning ? " Please reupload this dataset to refresh the scan" : "")
                    : ""}
              </div>
              {aiReadyWarning ? (
                <div className={styles.selectSubheader} style={{ marginLeft: "0px" }}>
                  {aiReadyWarning}
                </div>
              ) : (
                ""
              )}
              {hasAiMetrics ? (
                <React.Fragment>
                  {!hasIgnoredAiReady && (!isAiReady || !aiReadyFields.length) ? (
                    <div className={styles.selectSubheader} style={{ marginLeft: "0px" }}>
                      <strong>{"Quick tip:"}</strong>
                      &nbsp;
                      {" Try adding more data or manually cleaning this dataset"}
                    </div>
                  ) : (
                    ""
                  )}

                  <div className={styles.datasetReviewContent}>
                    <ReviewDatasetItem
                      error={!isAiReady}
                      noIcon={!isAiReady && hasIgnoredAiReady}
                      text={selectedDataset.outcome}
                    />
                    <div className={styles.datasetReviewDivider} />
                    <div className={styles.datasetReviewSubContent} style={{ maxHeight: "unset" }}>
                      <div className={styles.selectSubheader} style={{ marginLeft: "0px" }}>
                        {"Aible found " +
                          aiReadyFields.length +
                          " " +
                          handlePlurals(aiReadyFields.length, "column") +
                          " that are possible predictors of your outcome"}
                      </div>
                      <ExpansionItem
                        limit={
                          isAiReady
                            ? Math.min(6, aiReadyFields.length + 1)
                            : aiReadyFields.length
                              ? Math.min(6, aiReadyFields.length + 1)
                              : 0
                        }
                      >
                        <ReviewDatasetItem
                          style={{ width: "100%" }}
                          noIcon
                          text={
                            <React.Fragment>
                              <ProblematicPredictorLabel noMargin noIgnore label={<strong>{"Column"}</strong>} />
                              <ProblematicPredictorLabel
                                noMargin
                                noIgnore
                                noTitle
                                label={<strong>{"Information Score"}</strong>}
                                info={
                                  "The AI Validation step checks to see if there is sufficient predictive power in any of your variables for building an AI model. It's okay if your data is messy, there can still be enough to build AI models. Each variable is scored on a scale of 0 - 10."
                                }
                                maxWidth={130}
                              />
                            </React.Fragment>
                          }
                        />
                        {aiReadyFields.map((w, i) => (
                          <ReviewDatasetItem
                            style={{ width: "100%" }}
                            isExpand
                            noIcon
                            key={i}
                            text={
                              <React.Fragment>
                                <ProblematicPredictorLabel
                                  noMargin
                                  noIgnore
                                  label={w.label}
                                  warning={this.checkIsProblematicPredictor(w.label)}
                                />
                                <ProblematicPredictorBar value={w.value} isScore barColor={"#5BCF74"} />
                              </React.Fragment>
                            }
                          />
                        ))}
                        {notAiReadyFields.map((w, i) => (
                          <ReviewDatasetItem
                            isExpand
                            noIcon
                            error
                            key={i}
                            text={
                              <React.Fragment>
                                <ProblematicPredictorLabel
                                  noMargin
                                  noIgnore
                                  label={w.label}
                                  warning={this.checkIsProblematicPredictor(w.label)}
                                />
                                <ProblematicPredictorBar
                                  value={w.value}
                                  isScore
                                  barColor={"#F8CD7E"}
                                  color={"#4B495B"}
                                />
                              </React.Fragment>
                            }
                          />
                        ))}
                      </ExpansionItem>
                    </div>
                  </div>
                </React.Fragment>
              ) : (
                ""
              )}
            </Accordion>
            {isForecastReady && (
              <Accordion
                header={
                  <ReviewDatasetHeader
                    error={false}
                    header={"Forecast Ready"}
                    style={{ marginBottom: "0px" }}
                  ></ReviewDatasetHeader>
                }
                shrinkHeader
              >
                <div className={styles.selectSubheader} style={{ marginLeft: "0px", marginTop: "15px" }}>
                  {"Aible found " +
                    forecastOptions.length +
                    " column" +
                    (forecastOptions.length === 1 ? "" : "s") +
                    " that could be used to forecast " +
                    selectedDataset.outcome}
                </div>
                <div className={styles.datasetReviewDivider} />
                <div className={styles.datasetReviewSubContent}>
                  {forecastOptions.map((o, i) => (
                    <ReviewDatasetItem key={o.value} text={o.label} noIcon />
                  ))}
                </div>
              </Accordion>
            )}
          </ReviewDatasetColumn>
          {selectedDataset ? (
            <ReviewDatasetColumn isLarge>
              <ProblematicPredictors
                id={id}
                selectedDataset={selectedDataset}
                problematicPredictors={problematicPredictors}
                predictors={predictors}
                updateIgnored={updateIgnored}
                ignored={ignored}
                isDataMode={isDataMode}
                options={options}
                expandableDescription
              />
            </ReviewDatasetColumn>
          ) : (
            ""
          )}
          {!isForecast && isRetrain !== true && isEnabled("aible-explore") !== true && (
            <ReviewDatasetColumn>
              <ReviewDatasetHeader header={"Subgroup Evaluation"} icon={<Icon type={"layers"} size={22} />} />
              <div className={styles.selectSubheader}>
                {
                  "Get a more focused analysis by splitting the the analysis into groups like region, promotion type, or business unit."
                }
              </div>
              <div style={{ marginBottom: "10px" }}>
                <strong>{"(Optional) "}</strong>
                {"I would like to evaluate by:"}
              </div>
              <div className={styles.splitField}>
                <Select
                  menuPlacement={"top"}
                  options={splitOptions}
                  isClearable
                  onChange={(val) => updateSplitColumn(val ? val.value : val)}
                  isDisabled={hasProjectSplits}
                  value={
                    hasProjectSplits
                      ? {
                          label: overrideSplitColumn,
                          value: overrideSplitColumn
                        }
                      : splitColumn
                        ? { label: splitColumn, value: splitColumn }
                        : undefined
                  }
                  styles={{
                    menuList: (provided, state) => ({
                      ...provided,
                      maxHeight: "300px"
                    })
                  }}
                />
              </div>
              {isNotMatchingColumn && (
                <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                  <Icon type={"warning"} />
                  <span style={{ margin: "0px 6px" }}>{`Could not find the column`}</span>
                  <strong>{splitColumn}</strong>
                  <span style={{ marginLeft: "6px" }}>{`in the dataset.`}</span>
                </div>
              )}
              {project && !hasProjectSplits ? (
                <Button
                  onClick={() => (isNext ? createAndUpdateProject() : createSplits(splitColumn))}
                  style={{ marginTop: "6px" }}
                >
                  {"Create Subgroup"}
                </Button>
              ) : (
                ""
              )}
              {isEnabled("fairness") && !isRegressionDataset && (hasProjectSplits || splitColumn) ? (
                <div className={styles.fairnessToggle}>
                  <span style={{ marginRight: "8px" }}>{"Enable fairness metrics?"}</span>
                  <Toggle
                    checked={enableFairnessMetrics}
                    onChange={() => {
                      updateEnableFairnessMetrics(!enableFairnessMetrics);
                    }}
                    disabled={isFinite(id)}
                  />
                </div>
              ) : (
                <React.Fragment />
              )}
            </ReviewDatasetColumn>
          )}
        </div>
      </div>
    );
  }

  render() {
    const {
      id,
      isShow,
      isModelsNext,
      selectedDataset,
      datasetView,
      updateSelection,
      isRetrain,
      nextBlueprintId,
      isDataSelect,
      isOnlyLanguage
    } = this.props;

    const noDatasetOutcome =
      selectedDataset && selectedDataset.status && selectedDataset.status.indexOf("missing_target_variable") !== -1;
    const finishedIndexing =
      selectedDataset &&
      selectedDataset.status &&
      selectedDataset.status.indexOf("indexing") === -1 &&
      selectedDataset.status.indexOf("finishing_indexing") === -1 &&
      !selectedDataset.stack_id;
    const isError = selectedDataset && selectedDataset.errors && selectedDataset.errors.length > 0;
    const isDone = selectedDataset && (selectedDataset.rowCount !== -1 || (finishedIndexing && noDatasetOutcome));
    const isLoading = !isDone && !isError;

    if (isDataSelect) {
      return (
        <div className={styles.chooseDataset} style={{ width: "100%" }}>
          <SelectDataset
            id={id}
            selectedDataset={undefined}
            datasetView={datasetView}
            updateSelection={updateSelection}
            hideHeader
          />
        </div>
      );
    }

    return (
      <div className={styles.datasetReview} style={{ width: "100%", opacity: isShow === true ? 1 : 0 }}>
        {isOnlyLanguage !== true && isRetrain !== true && isModelsNext && nextBlueprintId !== undefined && (
          <div className={styles.chooseDataset} style={selectedDataset ? {} : { width: "100%" }}>
            <SelectDataset
              id={id}
              selectedDataset={selectedDataset}
              datasetView={datasetView}
              updateSelection={updateSelection}
              hideHeader
            />
          </div>
        )}
        <div id="academicReviewDataset" style={{ width: "100%" }}>
          <div
            className={styles.academicDatasetWrapper + " " + styles.reviewDatasetWrapper}
            style={selectedDataset ? {} : { marginTop: "0px" }}
          >
            {isRetrain !== true && isModelsNext !== true && (
              <div className={styles.chooseDataset} style={selectedDataset ? {} : { width: "100%" }}>
                <SelectDataset
                  id={id}
                  selectedDataset={selectedDataset}
                  datasetView={datasetView}
                  updateSelection={updateSelection}
                  hideHeader
                />
              </div>
            )}
            {selectedDataset ? isLoading ? <DatasetLoading /> : this.renderDatasetInfo() : <React.Fragment />}
          </div>
        </div>
      </div>
    );
  }
}

class DatasetLoading extends React.PureComponent {
  render() {
    return (
      <div className={styles.datasetLoadingWrapper}>
        <div className={styles.datasetLoadingText}>{"Evaluating Data..."}</div>
        <LoadingIndicator style={{ marginTop: "15px" }} />
      </div>
    );
  }
}

export class ReviewDatasetColumn extends React.PureComponent {
  render() {
    const { className, header, minWidth, style, noGrow, isLarge, ...retProps } = this.props;
    const retStyle = {
      ...style
    };
    if (noGrow) {
      retStyle.flexGrow = 0;
      retStyle.flexShrink = 0;
    }
    if (isLarge) {
      retStyle.flexBasis = 400;
      retStyle.maxWidth = 500;
    }
    return (
      <div
        {...this.retProps}
        className={styles.reviewDatasetColumn + (className ? " " + className : "")}
        style={retStyle}
      >
        {header ? <div className={styles.reviewDatasetColumnHeader}>{header}</div> : ""}
        <React.Fragment>{this.props.children}</React.Fragment>
      </div>
    );
  }
}

export class ReviewDatasetHeader extends React.PureComponent {
  render() {
    const { className, error, header, icon, noGrow, style, children } = this.props;
    const retStyle = {
      ...style
    };
    if (noGrow) {
      retStyle.flexGrow = 0;
      retStyle.flexShrink = 0;
    }
    return (
      <div className={styles.datasetReviewHeader + (className ? " " + className : "")} style={retStyle}>
        {icon ? (
          icon
        ) : (
          <Icon
            type={error ? "times" : "check"}
            isRound
            background={error ? "red" : "green"}
            style={error ? { padding: "5px" } : undefined}
            size={24}
          />
        )}
        {children ? <React.Fragment>{children}</React.Fragment> : <span>{header}</span>}
      </div>
    );
  }
}

export class ReviewDatasetItem extends React.PureComponent {
  render() {
    const { className, error, text, overlayTitle, overlay, icon, noIcon, isExpand, ...retProps } = this.props;
    return (
      <div {...retProps} className={styles.datasetReviewContent + (className ? " " + className : "")}>
        <div className={styles.datasetReviewHeaderContent}>
          {noIcon ? (
            ""
          ) : icon ? (
            icon
          ) : (
            <Icon type={error ? "timesRed" : "checkGreen"} isRound style={{ flexShrink: 0 }} />
          )}
          <span
            className={styles.datasetReviewHeaderText}
            style={{
              margin: noIcon ? "0px" : undefined,
              width: isExpand ? "100%" : undefined
            }}
          >
            {text}
          </span>
          {overlay ? (
            <OverlayTrigger
              placement={"top"}
              trigger={["click"]}
              rootClose
              overlay={
                <Popover id="popover-positioned-top" title={overlayTitle}>
                  {overlay}
                </Popover>
              }
            >
              <FontAwesomeIcon icon={faInfo} className={styles.typeIcon} style={{ marginLeft: "6px" }} />
            </OverlayTrigger>
          ) : (
            ""
          )}
        </div>
      </div>
    );
  }
}

export class ExpansionItem extends React.Component {
  state = {
    show: false
  };

  render() {
    const { className, label, limit, showAll, updateShowAll, children, ...retProps } = this.props;
    const { show } = this.state;
    const retlabel = label === undefined ? "View all columns" : label;
    const retLimit = isNaN(limit) ? 5 : limit;

    const retShow = showAll !== undefined ? showAll : show;
    return (
      <div
        {...retProps}
        className={styles.expansionWrapper + " " + (retShow ? styles.show : "") + " " + (className ? className : "")}
      >
        <div className={styles.expansionBody + " " + (retShow ? styles.show : "")}>
          {React.Children.map(children, (child, i) => {
            if (retShow || i < retLimit) {
              return child;
            } else {
              return "";
            }
          })}
        </div>
        <div className={styles.expansionFooter + " " + (retShow ? styles.show : "")}>
          <a
            onClick={() => {
              if (updateShowAll) {
                updateShowAll(!retShow);
              }
              this.setState({ show: !retShow });
            }}
          >
            {retShow ? "Hide" : retlabel}
          </a>
        </div>
      </div>
    );
  }
}
